import Grid from '@mui/material/GridLegacy';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import {
    HELPFUL_LINKS,
    MISSING_LICENSE,
    MISSING_LICENSE_DETAIL,
    OOPS,
    PAGE_NOT_FOUND,
    PAGE_NOT_FOUND_DETAILED,
} from '../strings';
import { ErrorSvg, RestrictedSvg } from '../svg';
import { useStyles } from './ErrorScreen.styles';

export interface LinkData {
    msg: string;
    href: string;
    self?: boolean; // Set when opening link in view
}
type ErrorImage = 'error' | 'restricted';
export type Props = {
    msg?: string;
    header?: string;
    details?: React.ReactNode;
    links?: LinkData[];
    automationIdPrefix?: string;
    titleBelow?: boolean;
    errorImage?: ErrorImage;
};

export function ErrorScreen({
    automationIdPrefix = '',
    details,
    errorImage = 'error',
    header,
    links,
    msg,
    titleBelow = false,
}: Props) {
    const headerMsg = header || OOPS;
    const { classes } = useStyles();

    const applyTrace = (suffix: string) => ({
        'automation-id': automationIdPrefix ? `${automationIdPrefix}-${suffix}` : suffix,
    });

    let errorSrc = <ErrorSvg />;
    if (errorImage === 'restricted') {
        errorSrc = <RestrictedSvg />;
    }

    return (
        <Grid container justifyContent="center" alignItems="center" className={classes.root}>
            <Grid item xs={12} className={classes.image} justifyContent="center">
                <div className={classes.image}>{errorSrc}</div>
                {!titleBelow && (
                    <div className={classes.floatingTextBlock}>
                        <Typography
                            variant="h1"
                            className={classes.title}
                            {...applyTrace('error-header')}
                        >
                            {headerMsg}
                        </Typography>
                        <Typography
                            variant="h2"
                            className={classes.subtitle}
                            {...applyTrace('error-msg')}
                        >
                            {msg}
                        </Typography>
                    </div>
                )}
            </Grid>
            <Grid item xs={12} sm={8} md={6} className={classes.messageBlock}>
                {titleBelow && (
                    <Typography
                        variant="h1"
                        className={classes.titleBelow}
                        {...applyTrace('error-msg')}
                    >
                        {msg}
                    </Typography>
                )}
                {details && (
                    <Typography variant="subtitle1" {...applyTrace('error-details')}>
                        {details}
                    </Typography>
                )}
                {links && links.length > 0 && (
                    <>
                        <Typography variant="subtitle1">{HELPFUL_LINKS}</Typography>
                        <Grid container direction="row" justifyContent="center">
                            {links.map((link) => (
                                <Grid item key={link.msg} padding={2}>
                                    <Link
                                        href={link.href}
                                        target={link?.self ? '_self' : '_blank'}
                                        color="primary"
                                        rel="noopener"
                                        underline="always"
                                    >
                                        {link.msg}
                                    </Link>
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
}

export function RestrictedScreen({
    header,
    msg,
    details,
    links,
}: Omit<Props, 'errorImage, titleBelow'>) {
    return (
        <ErrorScreen
            header={header}
            msg={msg || MISSING_LICENSE}
            details={details || MISSING_LICENSE_DETAIL}
            links={links}
            titleBelow
            errorImage="restricted"
        />
    );
}

export function NotFoundErrorScreen({
    header,
    msg,
    details,
    links,
    titleBelow,
}: Omit<Props, 'errorImage, '>) {
    return (
        <ErrorScreen
            header={header || OOPS}
            msg={msg || PAGE_NOT_FOUND}
            details={details || PAGE_NOT_FOUND_DETAILED}
            links={links}
            titleBelow={titleBelow}
        />
    );
}
